import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = getApiUrl();

/**
 * <OverlayInstanceSelectors />
 * Renders two "Before content" + two "After content" dropdowns. Each dropdown
 * lists EVERY admin-created module instance (any type — header, navigation,
 * footer, hero, text, etc.) plus a "None" option. Admin picks freely.
 *
 * Props:
 *   pre   — array (up to 2) of instance_ids to show before content
 *   post  — array (up to 2) of instance_ids to show after content
 *   onChange(next) — called with `{ pre_instances: [...], post_instances: [...] }`
 */
export default function OverlayInstanceSelectors({ pre = [], post = [], onChange }) {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/module-instances`, { withCredentials: true });
        if (cancelled) return;
        setInstances(r.data?.instances || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const inst of instances) {
      const key = inst.module_type || "other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(inst);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [instances]);

  const update = (side, idx, value) => {
    const list = side === "pre" ? [...pre] : [...post];
    list[idx] = value === "__none__" ? "" : value;
    // Strip empties and dupes; cap to 2.
    const cleaned = list.filter((v) => !!v);
    const dedup = cleaned.filter((v, i, a) => a.indexOf(v) === i).slice(0, 2);
    onChange({
      pre_instances: side === "pre" ? dedup : pre.filter(Boolean),
      post_instances: side === "post" ? dedup : post.filter(Boolean),
    });
  };

  const renderSelect = (side, idx, label) => {
    const list = side === "pre" ? pre : post;
    const value = list[idx] || "__none__";
    const testId = `${side}-instance-${idx}`;
    return (
      <div className="space-y-1">
        <Label className="text-xs">{label}</Label>
        <Select value={value} onValueChange={(v) => update(side, idx, v)}>
          <SelectTrigger data-testid={testId} className="h-9">
            <SelectValue placeholder={loading ? "Loading…" : "None"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— None —</SelectItem>
            {grouped.map(([type, list]) => (
              <div key={type}>
                <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/40">
                  {type.replace(/_/g, " ")} — Module Instances
                </div>
                {list.map((inst) => (
                  <SelectItem key={inst.instance_id} value={inst.instance_id}>
                    <span className="font-medium">{inst.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      · {inst.module_type.replace(/_/g, " ")} instance
                    </span>
                  </SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  return (
    <div className="space-y-4 border rounded-md p-4 bg-muted/20">
      <div>
        <p className="text-sm font-medium">Page Layout — Module Instances</p>
        <p className="text-xs text-muted-foreground mt-1">
          Optionally show up to two module instances before and after the page&apos;s main content.
          Any instance type can be picked here.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Before content</p>
          {renderSelect("pre", 0, "Slot #1")}
          {renderSelect("pre", 1, "Slot #2")}
        </div>
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">After content</p>
          {renderSelect("post", 0, "Slot #1")}
          {renderSelect("post", 1, "Slot #2")}
        </div>
      </div>
    </div>
  );
}
